"use client";

import { InlineMessage } from "@fanduel/formation-react-components";
import { GeneralStats } from "@fanduel/formation-react-icons";
import { fanduel as fd } from "@fanduel/formation-tokens";
import { useState } from "react";
import { Card, CardHeader } from "@/components/headToHead/Card";
import { SCREENS } from "@/constants/breakpoints";
import { Event } from "@/graphql/gql/graphql";
import { pickContrastingTeamColors } from "@/utils/colorContrastChecker";
import useMedia from "@/utils/hooks/useMedia";
import { TopLevelEvent } from "../types";
import SingleMarketCard, { MarketName } from "./SingleMarketCard";
import TeamsControl from "./TeamsControl";
import { deconstructHistoricalOdds, sortOdds } from "./utils";
import styles from "./lineChart.module.scss";

export enum TeamDisplay {
  Home = "home",
  Away = "away",
  Both = "both",
}

interface LineMovementProps {
  gameId: number;
  odds?: Event["odds"];
  historicalOdds?: Event["historicalOdds"];
  homeTeamProp?: TopLevelEvent["homeTeam"];
  awayTeamProp?: TopLevelEvent["awayTeam"];
}

function LineMovement({
  homeTeamProp,
  awayTeamProp,
  historicalOdds,
  odds,
  gameId,
}: LineMovementProps) {
  const [show, setShow] = useState<TeamDisplay>(TeamDisplay.Both);
  const isMobile = useMedia(`(max-width: ${SCREENS.SM})`);
  const homeTeam = { ...homeTeamProp };
  const awayTeam = { ...awayTeamProp };
  let homeShortName = homeTeam?.name?.split(" ").at(-1) ?? "Home";
  let awayShortName = awayTeam?.name?.split(" ").at(-1) ?? "Away";

  const { homeColor, awayColor } = pickContrastingTeamColors(
    {
      primaryColor: homeTeam?.primaryColor,
      secondaryColor: homeTeam?.secondaryColor,
    },
    {
      primaryColor: awayTeam?.primaryColor,
      secondaryColor: awayTeam?.secondaryColor,
    },
    fd.colors.backgroundSurface
  );
  homeTeam.primaryColor = homeColor;
  awayTeam.primaryColor = awayColor;

  if (isMobile || homeShortName.length + awayShortName.length > 14) {
    homeShortName = homeTeam?.abbreviation ?? "Home";
    awayShortName = awayTeam?.abbreviation ?? "Away";
  }

  const { home: homeMoneylineOdds, away: awayMoneylineOdds } = sortOdds(
    odds?.moneyline ?? null,
    homeTeam?.numberfireId,
    awayTeam?.numberfireId
  );

  const { home: homeSpreadOdds, away: awaySpreadOdds } = sortOdds(
    odds?.spread ?? null,
    homeTeam?.numberfireId,
    awayTeam?.numberfireId
  );

  const underTotalPointsOdds = odds?.totalPoints?.find((selection) => {
    return selection?.runnerName === "Under";
  });
  const overTotalPointsOdds = odds?.totalPoints?.find((selection) => {
    return selection?.runnerName === "Over";
  });

  const {
    days,
    homeMoneyLineOdds,
    homeSpreadLine,
    homeSpreadLineOdds,
    totalLine,
    totalOverOdds,
    totalUnderOdds,
    awayMoneyLineOdds,
    awaySpreadLine,
    awaySpreadLineOdds,
  } = deconstructHistoricalOdds(historicalOdds);

  return (
    <Card>
      <CardHeader
        icon={<GeneralStats width={17} height={17} />}
        title="Line Movement"
        action={
          <TeamsControl
            awayName={awayTeam?.name ?? "Away"}
            awayShortName={awayShortName}
            homeName={homeTeam?.name ?? "Home"}
            homeShortName={homeShortName}
            show={show}
            setShow={setShow}
            isMobile={isMobile}
          />
        }
      />
      <div className={styles.content}>
        <InlineMessage className={styles.info} intent="info">
          These graphs show how the spread, moneyline, and total have shifted at
          FanDuel Sportsbook over time.
        </InlineMessage>
        <div className={styles.cards}>
          <SingleMarketCard
            gameId={gameId}
            market={MarketName.Spread}
            days={days}
            home={{
              color: homeTeam?.primaryColor ?? fd.colors.brandPrimaryDefault,
              name: homeShortName,
              line: homeSpreadLine,
              odds: homeSpreadLineOdds,
              oddsButtonData: homeSpreadOdds,
            }}
            away={{
              color: awayTeam?.primaryColor ?? fd.colors.contentStrong,
              name: awayShortName,
              line: awaySpreadLine,
              odds: awaySpreadLineOdds,
              oddsButtonData: awaySpreadOdds,
            }}
            show={show}
            showToggle
          />
          <SingleMarketCard
            gameId={gameId}
            market={MarketName.Moneyline}
            days={days}
            home={{
              color: homeTeam?.primaryColor ?? fd.colors.brandPrimaryDefault,
              name: homeShortName,
              odds: homeMoneyLineOdds,
              oddsButtonData: homeMoneylineOdds,
            }}
            away={{
              color: awayTeam?.primaryColor ?? fd.colors.contentStrong,
              name: awayShortName,
              odds: awayMoneyLineOdds,
              oddsButtonData: awayMoneylineOdds,
            }}
            show={show}
          />
          <SingleMarketCard
            gameId={gameId}
            market={MarketName.Total}
            days={days}
            home={{
              color: fd.colors.brandPrimaryDefault,
              name: "Under",
              odds: totalUnderOdds,
              line: totalLine,
              oddsButtonData: underTotalPointsOdds,
            }}
            away={{
              color: fd.colors.contentStrong,
              name: "Over",
              odds: totalOverOdds,
              line: totalLine,
              oddsButtonData: overTotalPointsOdds,
            }}
            show={show}
            showToggle
          />
        </div>
      </div>
    </Card>
  );
}

export default LineMovement;
